import { formatDollars } from '@fruitful/fruitils';
import { type SpendGoalsWidgetProps } from '@fruitful/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CircleCheck,
    H6Regular,
    LabelRegular,
    Shadows,
    Slider,
    View,
    XStack,
    YStack,
} from 'smoothie';

export interface SpendGoalsSplit {
    monthlySpendCents: number;
    monthlyGoalsSpendCents: number;
}

export interface SpendGoalsWidgetComponentProps extends SpendGoalsWidgetProps {
    /** Omit to render the confirm button disabled (read-only views). */
    onConfirm?: (values: SpendGoalsSplit) => void;
    onSlideEnd?: (monthlySpendCents: number) => void;
    isConfirmDisabled?: boolean;
}

export const SpendGoalsWidget = ({
    monthlyTakeHomeCents,
    remainingCents,
    confirmedMonthlySpendCents,
    confirmedMonthlyGoalsSpendCents,
    isConfirmed: isConfirmedProp = false,
    onConfirm,
    onSlideEnd,
    isConfirmDisabled = false,
}: SpendGoalsWidgetComponentProps) => {
    const [hasConfirmed, setHasConfirmed] = useState(isConfirmedProp);

    // determine step size based on remaining cents
    // <$2000: $10
    // >=$2000: $20
    const stepCents = useMemo(
        () => (remainingCents < 200000 ? 1000 : 2000),
        [remainingCents],
    );

    const roundToStep = useCallback(
        (v: number) => Math.round(v / stepCents) * stepCents,
        [stepCents],
    );
    const defaultSplitValue = useMemo(
        () => roundToStep(remainingCents / 2),
        [remainingCents, roundToStep],
    );
    const initialSpendCents = confirmedMonthlySpendCents ?? defaultSplitValue;
    const [sliderValue, setSliderValue] = useState(initialSpendCents);

    const initialGoalsCents =
        confirmedMonthlyGoalsSpendCents ?? remainingCents - initialSpendCents;

    const [monthlySpendCents, setMonthlySpendCents] =
        useState(initialSpendCents);
    const [monthlyGoalsSpendCents, setMonthlyGoalsSpendCents] =
        useState(initialGoalsCents);

    useEffect(() => {
        if (hasConfirmed) {
            return;
        }

        setMonthlySpendCents(sliderValue);
        setMonthlyGoalsSpendCents(remainingCents - sliderValue);
    }, [hasConfirmed, remainingCents, sliderValue]);

    return (
        <YStack
            padding={16}
            alignSelf="center"
            width="100%"
            maxWidth={345}
            backgroundColor="rgba(255, 255, 255, 0.5)"
            borderRadius={24}
            borderWidth={1}
            borderColor="$white"
            alignItems="center"
            gap={8}
            {...Shadows.SMALL}
        >
            <H6Regular>Spend + Goals</H6Regular>
            <BodyMediumRegular>Adjust to what feels right</BodyMediumRegular>
            <XStack
                width="100%"
                alignItems="center"
                justifyContent="space-between"
                gap={8}
            >
                <XStack gap={8} alignItems="center">
                    <View
                        width={8}
                        height={8}
                        borderRadius={4}
                        backgroundColor="$leaf"
                    />
                    <LabelRegular>Spend</LabelRegular>
                </XStack>
                <XStack gap={8} alignItems="center">
                    <LabelRegular>Goals</LabelRegular>
                    <View
                        width={8}
                        height={8}
                        borderRadius={4}
                        backgroundColor="$petal"
                    />
                </XStack>
            </XStack>
            <Slider
                value={[sliderValue]}
                defaultValue={[initialSpendCents]}
                onSlideMove={(_, slideValue: number) => {
                    if (!hasConfirmed) {
                        setSliderValue(roundToStep(slideValue));
                    }
                }}
                onSlideEnd={() => {
                    if (!hasConfirmed) {
                        setSliderValue((prev) => {
                            const value = roundToStep(prev);
                            onSlideEnd?.(value);
                            return value;
                        });
                    }
                }}
                min={stepCents}
                max={remainingCents - stepCents}
                step={stepCents}
                width="100%"
                trackActiveColor="$leaf"
                trackColor="$petal"
                thumbSize={24}
                disabled={hasConfirmed}
            />
            <XStack
                width="100%"
                alignItems="center"
                justifyContent="space-between"
            >
                <YStack gap={4}>
                    <BodyLargeEmphasized>
                        {formatDollars(monthlySpendCents / 100, false)}
                    </BodyLargeEmphasized>
                    <LabelRegular color="$gray1000">
                        {Math.floor(
                            (monthlySpendCents / monthlyTakeHomeCents) * 100,
                        )}
                        % of income
                    </LabelRegular>
                </YStack>
                <YStack gap={4}>
                    <BodyLargeEmphasized textAlign="right">
                        {formatDollars(monthlyGoalsSpendCents / 100, false)}
                    </BodyLargeEmphasized>
                    <LabelRegular textAlign="right" color="$gray1000">
                        {Math.floor(
                            (monthlyGoalsSpendCents / monthlyTakeHomeCents) *
                                100,
                        )}
                        % of income
                    </LabelRegular>
                </YStack>
            </XStack>

            <Button
                variant={ButtonVariant.SECONDARY_ALT}
                size={ButtonSize.LARGE}
                text={hasConfirmed ? 'Confirmed' : 'Looks good'}
                disabled={
                    hasConfirmed || isConfirmDisabled || onConfirm == null
                }
                width="auto"
                icon={CircleCheck}
                onPress={() => {
                    if (hasConfirmed || onConfirm == null) {
                        return;
                    }

                    setHasConfirmed(true);
                    onConfirm({
                        monthlySpendCents,
                        monthlyGoalsSpendCents,
                    });
                }}
            />
        </YStack>
    );
};
